import * as THREE from 'three';
import { PLAYER, TEAM, LAYERS } from '../core/Constants.js';
import { EVENTS } from '../core/EventBus.js';

/**
 * Player - ESTADO do jogador (secao 4.8 do ARCHITECTURE.md).
 *
 * Responsabilidades:
 *   - vida, dano, regeneracao apos PLAYER.REGEN_DELAY, morte e reset
 *   - pontuacao e time
 *   - manter as hitboxes do jogador registradas em ctx.targets, para a IA
 *     conseguir mirar e acertar como acerta qualquer outra entidade
 *
 * O movimento NAO mora aqui: `position` e um espelho do PlayerController.
 *
 * Nota sobre dano: quem calcula o multiplicador de regiao (HITBOX_MULTIPLIER) e
 * a Balistica, nao este modulo. `damage()` recebe o valor JA final; `part` vem
 * junto so como informacao (HUD, killfeed, som).
 */

// Geometria do corpo, em metros, medida a partir do OLHO (que sobe e desce com o
// agachamento). Assim o corpo inteiro acompanha a postura sem conta extra.
const HEAD_RADIUS = 0.155;
const HEAD_OFFSET = 0.07;      // acima do olho
const CHEST_OFFSET = -0.30;
const STOMACH_OFFSET = -0.66;
const CHEST_SIZE = [0.50, 0.44, 0.30];
const STOMACH_SIZE = [0.44, 0.34, 0.28];
const LEG_SIZE = [0.42, 0.80, 0.30];
const LEG_CENTER = 0.40;       // a partir dos pes, em pe
const LEG_CROUCH_SCALE = 0.55; // pernas dobradas quando agachado

const REGEN_EVENT_INTERVAL = 0.25; // nao inundar o barramento a 60 Hz durante a cura

const _tmpVec = new THREE.Vector3();

export class Player {
  constructor(ctx) {
    this.ctx = ctx;

    this.maxHealth = PLAYER.MAX_HEALTH;
    this.health = this.maxHealth;
    this.alive = true;
    this.team = TEAM.PLAYER;
    this.score = 0;
    this.kills = 0;
    this.deaths = 0;

    // Marcadores que a IA e o killfeed usam para reconhecer quem e quem.
    this.isPlayer = true;
    this.name = 'Jogador';

    this.hitGroup = null;
    /** @type {Array<{part:string, object:THREE.Object3D}>} */
    this.hitboxes = [];
    this._parts = {};
    this._geometries = [];
    this._material = null;

    this._sinceDamage = PLAYER.REGEN_DELAY;
    this._regenPulse = 0;
    this._registered = false;
    this._warned = new Set();

    this._fallbackPos = new THREE.Vector3();
    this._eyePos = new THREE.Vector3();
    this._aimPos = new THREE.Vector3();
    this._lastFrom = new THREE.Vector3();
    this.lastDamageFrom = null;      // Vector3 do ultimo atirante, ou null
    this.lastAttacker = null;

    this._onDamage = this._onDamage.bind(this);
  }

  async init() {
    // Invisibilidade pelo MATERIAL, nao pelo objeto: o renderer pula o mesh do
    // mesmo jeito, mas `object.visible` continua true. Isso importa porque nao
    // se sabe se o TargetRegistry usa THREE.Raycaster (que ignora visible) ou um
    // teste proprio que descarta objeto invisivel - assim funciona nos dois casos.
    this._material = new THREE.MeshBasicMaterial({ visible: false });

    this.hitGroup = new THREE.Group();
    this.hitGroup.name = 'player-hitboxes';

    const head = new THREE.SphereGeometry(HEAD_RADIUS, 8, 6);
    const chest = new THREE.BoxGeometry(CHEST_SIZE[0], CHEST_SIZE[1], CHEST_SIZE[2]);
    const stomach = new THREE.BoxGeometry(STOMACH_SIZE[0], STOMACH_SIZE[1], STOMACH_SIZE[2]);
    const legs = new THREE.BoxGeometry(LEG_SIZE[0], LEG_SIZE[1], LEG_SIZE[2]);
    this._geometries.push(head, chest, stomach, legs);

    this._parts.head = this._makeHitbox('head', head);
    this._parts.chest = this._makeHitbox('chest', chest);
    this._parts.stomach = this._makeHitbox('stomach', stomach);
    this._parts.leg = this._makeHitbox('leg', legs);

    this.ctx.scene.add(this.hitGroup);
    this._layoutHitboxes(PLAYER.EYE_HEIGHT, 0);
  }

  _makeHitbox(part, geometry) {
    const mesh = new THREE.Mesh(geometry, this._material);
    mesh.name = `player-hit-${part}`;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    mesh.frustumCulled = false;
    // Fica na camada CHARACTER sem sair da camada 0: assim qualquer raycaster
    // acerta, tenha ele configurado camadas ou nao.
    mesh.layers.enable(LAYERS.CHARACTER);
    mesh.userData.entity = this;
    mesh.userData.part = part;
    mesh.userData.hitbox = true;
    mesh.userData.surface = 'flesh';
    this.hitGroup.add(mesh);
    this.hitboxes.push({ part, object: mesh });
    return mesh;
  }

  // -------------------------------------------------------------------------
  // Posicao (espelho do controller)
  // -------------------------------------------------------------------------

  /** Posicao dos PES. Para mirar no jogador, use `aimPoint` ou as hitboxes. */
  get position() {
    const p = this.ctx.controller?.position;
    return p || this._fallbackPos;
  }

  /** Posicao do olho (camera). Recalculada a cada leitura, nao aloca. */
  get eyePosition() {
    const p = this.position;
    const eye = this.ctx.controller?.eyeHeight ?? PLAYER.EYE_HEIGHT;
    return this._eyePos.set(p.x, p.y + eye, p.z);
  }

  /** Centro do peito - e para ca que a IA deve mirar. */
  get aimPoint() {
    const p = this.position;
    const eye = this.ctx.controller?.eyeHeight ?? PLAYER.EYE_HEIGHT;
    return this._aimPos.set(p.x, p.y + eye + CHEST_OFFSET, p.z);
  }

  // -------------------------------------------------------------------------
  // Vida
  // -------------------------------------------------------------------------

  /**
   * Aplica dano JA calculado (com multiplicador de regiao, queda por distancia,
   * penetracao - tudo isso e da Balistica).
   * @param {number} amount
   * @param {THREE.Vector3|{position:THREE.Vector3}|null} from  quem/de onde veio - para o indicador de direcao
   * @param {string} part  regiao atingida (informativa)
   * @returns {number} dano efetivamente aplicado
   */
  damage(amount, from, part = 'chest') {
    const amt = Number(amount);
    if (!this.alive || !Number.isFinite(amt) || amt <= 0) return 0;

    const applied = Math.min(this.health, amt);
    this.health -= applied;
    if (this.health < 0) this.health = 0;

    this._sinceDamage = 0;
    this._regenPulse = 0;

    const fromPos = this._resolvePosition(from);
    if (fromPos) {
      this._lastFrom.copy(fromPos);
      this.lastDamageFrom = this._lastFrom;
    } else {
      this.lastDamageFrom = null;
    }
    if (from && typeof from === 'object' && !from.isVector3) this.lastAttacker = from;

    // Evento em ritmo humano (nao por frame): clonar e seguro e evita que o HUD
    // guarde uma referencia que muda sozinha no proximo tiro.
    this.ctx.events.emit(EVENTS.PLAYER_DAMAGED, {
      amount: applied,
      from: fromPos ? fromPos.clone() : null,
      health: this.health,
      part
    });

    if (this.health <= 0) this._die(from);
    return applied;
  }

  /** Alias defensivo: sistemas diferentes costumam chamar de takeDamage. */
  takeDamage(amount, from, part) {
    return this.damage(amount, from, part);
  }

  heal(n) {
    const amt = Number(n);
    if (!this.alive || !Number.isFinite(amt) || amt <= 0) return 0;
    const before = this.health;
    this.health = Math.min(this.maxHealth, this.health + amt);
    const gained = this.health - before;
    if (gained > 0) this.ctx.events.emit(EVENTS.PLAYER_HEALED, { health: this.health });
    return gained;
  }

  addScore(n) {
    const amt = Number(n);
    if (!Number.isFinite(amt)) return this.score;
    this.score += amt;
    return this.score;
  }

  /** Volta a vida cheia e religa as hitboxes. Quem reposiciona e o GameState. */
  reset() {
    this.health = this.maxHealth;
    this.alive = true;
    // Comeca com a regeneracao ja liberada: acabou de nascer, nao "levou tiro".
    this._sinceDamage = PLAYER.REGEN_DELAY;
    this._regenPulse = 0;
    this.lastDamageFrom = null;
    this.lastAttacker = null;
    this._registered = false;
    this._ensureRegistered();
    if (this.hitGroup) this.hitGroup.visible = true;
    this.ctx.events.emit(EVENTS.PLAYER_HEALED, { health: this.health });
  }

  _die(source) {
    this.alive = false;
    this.health = 0;
    this.deaths++;

    // Sai do registro de alvos: bot nao gasta municao em cadaver.
    if (this._registered) {
      try { this.ctx.targets?.unregister?.(this); } catch (err) { this._warnOnce('unregister falhou', err); }
      this._registered = false;
    }
    if (this.hitGroup) this.hitGroup.visible = false;

    this.ctx.events.emit(EVENTS.PLAYER_DIED, { source: source ?? this.lastAttacker ?? null });
  }

  // -------------------------------------------------------------------------
  // Ciclo de frame
  // -------------------------------------------------------------------------

  update(dt) {
    // ctx.targets so existe depois deste modulo ser construido (ver ordem em
    // main.js), por isso o registro e preguicoso, no primeiro frame.
    this._ensureRegistered();
    if (!this.alive) return;

    // PARTIDA ONLINE: quem manda na vida e o servidor. A regeneracao local
    // precisa sair de cena aqui porque o dano remoto chega por NetSync, que
    // ATRIBUI player.health direto - sem passar por damage(), que e quem zera
    // _sinceDamage. Sem esta guarda, _sinceDamage esta sempre acima do atraso
    // de regeneracao e a barra volta para 100 em ~1,2 s enquanto o servidor
    // continua com o jogador ferido: ele ve vida cheia e morre "do nada".
    if (this.ctx.match?.isPlaying?.()) return;

    this._sinceDamage += dt;
    if (this.health >= this.maxHealth || this._sinceDamage < PLAYER.REGEN_DELAY) return;

    this.health = Math.min(this.maxHealth, this.health + PLAYER.REGEN_RATE * dt);
    this._regenPulse += dt;
    if (this.health >= this.maxHealth || this._regenPulse >= REGEN_EVENT_INTERVAL) {
      this._regenPulse = 0;
      this.ctx.events.emit(EVENTS.PLAYER_HEALED, { health: this.health });
    }
  }

  /**
   * Hitboxes acompanhando a camera. Roda em lateUpdate porque o CameraRig ja
   * definiu o olhar neste frame - assim o corpo nunca fica um frame atrasado
   * em relacao a mira do jogador.
   */
  lateUpdate() {
    if (!this.hitGroup || !this.alive) return;
    const ctrl = this.ctx.controller;
    const p = this.position;
    const eye = ctrl?.eyeHeight ?? PLAYER.EYE_HEIGHT;
    const crouch = ctrl?.crouchAmount ?? 0;
    const rawYaw = this.ctx.cameraRig?.getYaw?.();
    const yaw = (typeof rawYaw === 'number' && Number.isFinite(rawYaw)) ? rawYaw : 0;

    this.hitGroup.position.set(p.x, p.y, p.z);
    this.hitGroup.rotation.y = yaw;
    this._layoutHitboxes(eye, crouch);
    // Atualiza ja: a IA pode fazer raycast no fixedUpdate do proximo frame,
    // antes de o renderer percorrer a cena.
    this.hitGroup.updateMatrixWorld(true);
  }

  _layoutHitboxes(eye, crouch) {
    const legScale = 1 - (1 - LEG_CROUCH_SCALE) * crouch;
    this._parts.head.position.y = eye + HEAD_OFFSET;
    this._parts.chest.position.y = eye + CHEST_OFFSET;
    this._parts.stomach.position.y = eye + STOMACH_OFFSET;
    this._parts.leg.position.y = LEG_CENTER * legScale;
    this._parts.leg.scale.y = legScale;
  }

  // -------------------------------------------------------------------------
  // Integracao com ctx.targets
  // -------------------------------------------------------------------------

  _ensureRegistered() {
    if (this._registered || !this.alive) return;
    const targets = this.ctx.targets;
    if (!targets || typeof targets.register !== 'function') return;
    try {
      targets.register(this, {
        hitboxes: this.hitboxes,
        team: this.team,
        onDamage: this._onDamage
      });
      this._registered = true;
    } catch (err) {
      this._registered = true; // nao insistir a cada frame num registro quebrado
      this._warnOnce('registro em ctx.targets falhou', err);
    }
  }

  /**
   * Callback de dano vindo da Balistica, via TargetRegistry.
   * O formato exato do argumento nao esta fechado no contrato, entao aceitamos
   * as tres formas plausiveis: (info), (amount, info) e (amount, part, source).
   */
  _onDamage(a, b, c) {
    let amount = 0;
    let part = 'chest';
    let from = null;

    if (typeof a === 'number') {
      amount = a;
      if (typeof b === 'string') part = b;
      else if (b && typeof b === 'object') {
        if (typeof b.part === 'string') part = b.part;
        from = b.from ?? b.origin ?? b.source ?? b.shooter ?? null;
      }
      if (typeof c === 'string') part = c;
      else if (c && typeof c === 'object' && !from) from = c;
    } else if (a && typeof a === 'object') {
      const raw = a.amount ?? a.damage ?? a.value ?? 0;
      amount = Number(raw) || 0;
      if (typeof a.part === 'string') part = a.part;
      from = a.from ?? a.origin ?? a.source ?? a.shooter ?? a.attacker ?? null;
    }

    return this.damage(amount, from, part);
  }

  /** Aceita Vector3, entidade com .position, array [x,y,z] ou objeto {x,y,z}. */
  _resolvePosition(x) {
    if (!x) return null;
    if (x.isVector3) return x;
    if (x.position && x.position.isVector3) return x.position;
    if (Array.isArray(x) && x.length >= 3) return _tmpVec.set(x[0], x[1], x[2]);
    if (typeof x.x === 'number' && typeof x.y === 'number' && typeof x.z === 'number') {
      return _tmpVec.set(x.x, x.y, x.z);
    }
    if (x.position && typeof x.position.x === 'number') {
      return _tmpVec.set(x.position.x, x.position.y, x.position.z);
    }
    return null;
  }

  _warnOnce(msg, err) {
    if (this._warned.has(msg)) return;
    this._warned.add(msg);
    console.warn(`[Player] ${msg}`, err || '');
  }

  dispose() {
    if (this._registered) {
      try { this.ctx.targets?.unregister?.(this); } catch { /* registro ja foi */ }
      this._registered = false;
    }
    if (this.hitGroup) {
      this.hitGroup.parent?.remove(this.hitGroup);
      this.hitGroup = null;
    }
    for (const g of this._geometries) g.dispose();
    this._geometries.length = 0;
    this._material?.dispose();
    this._material = null;
    this.hitboxes.length = 0;
    this._parts = {};
  }
}
